// components/ui/Input.tsx — Floating label input with validation

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  error?: string;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onRightIconClick?: () => void;
  maxCharacters?: number;
  isSuccess?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      onRightIconClick,
      maxCharacters,
      isSuccess,
      className,
      id,
      value,
      ...props
    },
    ref
  ) => {
    const inputId = id ?? `input-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const charCount = typeof value === 'string' ? value.length : 0;
    const isNearLimit = maxCharacters ? charCount > maxCharacters * 0.85 : false;
    const isAtLimit   = maxCharacters ? charCount >= maxCharacters : false;

    return (
      <div className={styles.wrapper}>
        <div
          className={clsx(
            styles.field,
            leftIcon  && styles.hasLeftIcon,
            (rightIcon || onRightIconClick) && styles.hasRightIcon
          )}
        >
          <input
            ref={ref}
            id={inputId}
            value={value}
            placeholder=" " /* Space so :not(:placeholder-shown) works */
            className={clsx(
              styles.input,
              error && styles.error,
              isSuccess && styles.success,
              className
            )}
            aria-invalid={!!error}
            aria-describedby={clsx(error && errorId, helperText && helperId) || undefined}
            maxLength={maxCharacters}
            {...props}
          />

          <label className={styles.label} htmlFor={inputId}>
            {label}
          </label>

          {leftIcon && (
            <span className={styles.leftIcon} aria-hidden="true">
              {leftIcon}
            </span>
          )}

          {rightIcon && !onRightIconClick && (
            <span className={styles.rightIcon} aria-hidden="true">
              {rightIcon}
            </span>
          )}

          {rightIcon && onRightIconClick && (
            <button
              type="button"
              className={styles.rightIconBtn}
              onClick={onRightIconClick}
              tabIndex={-1}
              aria-label="Toggle"
            >
              {rightIcon}
            </button>
          )}

          {maxCharacters && (
            <span
              className={clsx(
                styles.charCount,
                isNearLimit && styles.nearLimit,
                isAtLimit && styles.atLimit
              )}
              aria-live="polite"
            >
              {charCount}/{maxCharacters}
            </span>
          )}
        </div>

        {error && (
          <p id={errorId} className={styles.errorText} role="alert">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className={styles.helperText}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
